import { LitElement, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import * as d3 from 'd3';

import {
  DataPointScale,
  DataPointPeriod,
  barProperties,
} from '../types/data-point.type';

const TRANSPARENT = 'transparent';
const GRAY = '#D9D9D9';
const WHITE = '#FFFFFF';
const GREEN = '#8dc63f';
const ORANGE = '#e36f1e';
const YELLOW = '#fdbb30';
const DEFAULT_BAR_HEIGHT_PX = 35;

interface DOMElements {
  background: unknown | undefined | null;
  bar?: unknown | undefined | null;
  barGreen?: unknown | undefined | null;
  barYellow?: unknown | undefined | null;
  barOrange?: unknown | undefined | null;
}

@customElement('apt-mini-bar')
export class AptMiniBar extends LitElement {
  @property({ type: Object })
  periodData: Array<DataPointPeriod | DataPointScale> = [];

  @property({ type: Boolean })
  isMultiColor = false;

  @property({ type: Object })
  miniBarProperties: barProperties = {
    barWidth: 8,
    barHeight: 30,
    canvasHeight: 50,
    canvasWidth: 70,
    barSpacing: 15,
  };

  @property({ type: String })
  barColor = GREEN;

  @property({ type: Boolean })
  enableTooltips = false;

  @property({ type: Boolean })
  enableDynamicBars = false;

  override render(): TemplateResult {
    if (!this.periodData || this.periodData?.length === 0) {
      return html`<div></div>`;
    }

    if (this.isMultiColor) {
      this.drawMultiColorBars();
    } else {
      this.drawBars();
    }

    return html`<div></div>`;
  }

  getSvgCanvas() {
    return d3
      .select(this.shadowRoot as unknown as Element)
      .append('svg')
      .attr('id', 'svg-mini-bars')
      .attr('width', `${this.miniBarProperties.canvasWidth}px`)
      .attr('height', `${this.miniBarProperties.canvasHeight}px`)
      .append('g');
  }

  drawBars(): DOMElements[] {
    const svg = this.getSvgCanvas();

    const periodTargets =
      this.periodData?.map((b) => (b as DataPointPeriod).target) ?? [];
    const periodAverages =
      this.periodData?.map((b) => (b as DataPointPeriod).actual) ?? [];

    const allPeriodsMaxValue = Math.max(...periodTargets, ...periodAverages);

    return this.periodData?.map((data, index) => {
      const actualData = data as DataPointPeriod;

      const periodMaxValue = Math.max(actualData.actual, actualData.target);

      let barHeight = this.miniBarProperties.barHeight;

      if (this.enableDynamicBars) {
        barHeight =
          (periodMaxValue * DEFAULT_BAR_HEIGHT_PX) / allPeriodsMaxValue;
      }

      const barScale = this.buildBarScale(0, periodMaxValue, barHeight);

      const background = this.buildBarBackground(
        svg,
        index,
        barScale,
        GRAY,
        actualData.tooltip,
      );

      const bar = this.buildBar(
        svg,
        index,
        actualData.actual,
        actualData.target - actualData.actual,
        barScale,
        this.barColor,
        actualData.tooltip,
      );

      return {
        background,
        bar,
      };
    });
  }

  drawMultiColorBars(): DOMElements[] {
    const svg = this.getSvgCanvas();

    let maxDataPoint = Math.max(
      ...(this.periodData as DataPointScale[]).map((currentScale) =>
        this.sumValues(currentScale),
      ),
    );
    maxDataPoint = maxDataPoint <= 0 ? 100 : maxDataPoint;

    return this.periodData.map((data, index) => {
      const actualData = data as DataPointScale;
      const totalPeriod: number = this.sumValues(actualData);

      const barScale = d3
        .scaleLinear()
        .domain([0, maxDataPoint])
        .range([0, this.miniBarProperties.canvasHeight]);

      const background = this.buildBarBackground(
        svg,
        index,
        barScale,
        totalPeriod > 0 ? TRANSPARENT : GRAY,
      );

      const barGreen = this.buildMulticolorBar(
        svg,
        index,
        actualData.on,
        maxDataPoint - actualData.on,
        actualData.on,
        barScale,
        GREEN,
      );
      const barYellow = this.buildMulticolorBar(
        svg,
        index,
        actualData.near,
        maxDataPoint - (actualData.on + actualData.near),
        actualData.near,
        barScale,
        YELLOW,
      );
      const barOrange = this.buildMulticolorBar(
        svg,
        index,
        actualData.under,
        maxDataPoint - (actualData.under + actualData.near + actualData.on),
        actualData.under,
        barScale,
        ORANGE,
      );

      return {
        barGreen,
        barYellow,
        barOrange,
        background,
      };
    });
  }

  buildMulticolorBar(
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    index: number,
    actual: number,
    y: number,
    height: number,
    scale: d3.ScaleLinear<number, number, never>,
    color: string,
  ) {
    const bar = svg
      .selectAll(null)
      .data([actual])
      .enter()
      .append('rect')
      .attr('x', () => index * this.miniBarProperties.barSpacing)
      .attr('y', y > 0 ? scale(y) : 0)
      .attr('width', this.miniBarProperties.barWidth)
      .attr('height', height > 0 ? scale(height) : 0)
      .style('fill', color);

    return bar;
  }

  buildBar(
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    index: number,
    actual: number,
    y: number,
    scale: d3.ScaleLinear<number, number, never>,
    color: string,
    tooltipText?: string,
  ) {
    const bar = svg
      .selectAll(null)
      .data([actual])
      .enter()
      .append('rect')
      .attr('x', () => index * this.miniBarProperties.barSpacing)
      .attr('y', () => this.miniBarProperties.barHeight - scale(actual))
      .attr('width', this.miniBarProperties.barWidth)
      .attr('height', scale(actual))
      .style('fill', color);

    if (this.enableTooltips) {
      bar.append('title').text(`${tooltipText}`);
    }

    return bar;
  }

  buildBarScale(from: number, to: number, barHeight: number) {
    return d3.scaleLinear().domain([from, to]).range([from, barHeight]);
  }

  buildBarBackground(
    svg: d3.Selection<SVGGElement, unknown, null, undefined>,
    i: number,
    scale: d3.ScaleLinear<number, number, never>,
    color: string,
    tooltipText?: string,
  ) {
    const canvasHeight = this.miniBarProperties.canvasHeight;

    const background = svg
      .selectAll(null)
      .data(this.periodData)
      .enter()
      .append('rect')
      .attr('x', () => i * this.miniBarProperties.barSpacing)
      .attr('y', () => canvasHeight - scale(scale.domain()[1]))
      .attr('width', this.miniBarProperties.barWidth)
      .attr('height', () => scale(scale.domain()[1]))
      .style('fill', color);

    if (this.enableTooltips) {
      background.append('title').text(`${tooltipText}`);
    }
    return background;
  }

  sumValues(data: DataPointScale): number {
    return Object.values(data).reduce(
      (accumulator: number, current: number) => accumulator + current,
      0,
    );
  }
}
